import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { TrendingUp, Users, Repeat, DollarSign } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => { const t = localStorage.getItem("cauldron_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

/**
 * Pro Analytics Dashboard — earnings, retention, top clients + services.
 * Pro-tier gated (returns 403 for Starter/free).
 */
export default function ProAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/pro/analytics?days=${days}`, { headers: headers() })
      .then(r => setData(r.data))
      .catch(e => { if (e.response?.status === 403) setGated(true); })
      .finally(() => setLoading(false));
  }, [days]);

  if (gated) {
    return <Layout><div className="max-w-2xl mx-auto p-8 text-center text-amber-200/70">
      <h1 className="text-2xl font-serif text-amber-100 mb-3">Advanced analytics are a Pro feature</h1>
      <a href="/pricing" className="text-amber-400 underline">Upgrade to Pro →</a>
    </div></Layout>;
  }
  if (loading) return <Layout><div className="p-8 text-amber-200/60">Loading…</div></Layout>;
  if (!data) return <Layout><div className="p-8 text-amber-200/60">No data available.</div></Layout>;

  const maxMonthGross = Math.max(...data.earnings.byMonth.map(m => m.gross), 1);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="pro-analytics">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-serif text-amber-100">Analytics</h1>
            <p className="text-sm text-amber-200/60">Earnings, retention, and top performers.</p>
          </div>
          <div className="flex gap-2">
            {[30, 90, 180, 365].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs rounded-md ${days === d ? "bg-amber-500 text-neutral-900" : "border border-neutral-800 text-amber-200"}`}
                data-testid={`range-${d}`}
              >{d}d</button>
            ))}
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="kpi-tiles">
          <KpiTile testid="kpi-gross" icon={<DollarSign className="h-5 w-5" />} label="Gross earnings" value={`$${data.earnings.totalGross.toFixed(2)}`} />
          <KpiTile testid="kpi-net" icon={<TrendingUp className="h-5 w-5" />} label="Net (after fee)" value={`$${data.earnings.totalNet.toFixed(2)}`} />
          <KpiTile testid="kpi-clients" icon={<Users className="h-5 w-5" />} label="Unique clients" value={data.retention.uniqueClients} />
          <KpiTile testid="kpi-repeat" icon={<Repeat className="h-5 w-5" />} label="Repeat rate" value={`${(data.retention.repeatRate * 100).toFixed(0)}%`} hint={`${data.retention.repeatClients} of ${data.retention.uniqueClients}`} />
        </div>

        {/* Monthly bar chart */}
        <Card className="p-5 bg-neutral-900/50 border-neutral-800">
          <h3 className="text-amber-100 font-serif mb-4">Monthly earnings</h3>
          {data.earnings.byMonth.length === 0 ? (
            <div className="text-amber-200/50 text-sm">No bookings yet in this range.</div>
          ) : (
            <div className="space-y-2" data-testid="monthly-chart">
              {data.earnings.byMonth.map(m => (
                <div key={m.month} className="flex items-center gap-3" data-testid={`month-${m.month}`}>
                  <div className="w-16 text-xs text-amber-200/60 font-mono">{m.month}</div>
                  <div className="flex-1 h-6 bg-neutral-800/60 rounded-md overflow-hidden">
                    <div
                      className="h-full bg-amber-500/70 flex items-center px-2 text-[10px] text-neutral-900 font-medium"
                      style={{ width: `${Math.max(4, (m.gross / maxMonthGross) * 100)}%` }}
                    >
                      ${m.gross.toFixed(0)}
                    </div>
                  </div>
                  <div className="w-16 text-xs text-amber-200/60 text-right">{m.count} bkg</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top clients */}
          <Card className="p-5 bg-neutral-900/50 border-neutral-800">
            <h3 className="text-amber-100 font-serif mb-4">Top clients</h3>
            {data.topClients.length === 0 ? (
              <div className="text-amber-200/50 text-sm">No clients yet.</div>
            ) : (
              <ul className="space-y-2" data-testid="top-clients">
                {data.topClients.map((c, i) => (
                  <li key={c.clientId} className="flex items-center gap-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`top-client-${i}`}>
                    <div className="text-amber-500 text-sm font-serif w-4">#{i + 1}</div>
                    <div className="flex-1">
                      <div className="text-sm text-amber-100">{c.name}</div>
                      <div className="text-xs text-amber-200/50">{c.bookingCount} bookings</div>
                    </div>
                    <div className="text-amber-100 text-sm font-serif">${c.totalSpent.toFixed(2)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Top services */}
          <Card className="p-5 bg-neutral-900/50 border-neutral-800">
            <h3 className="text-amber-100 font-serif mb-4">Top services</h3>
            {data.topServices.length === 0 ? (
              <div className="text-amber-200/50 text-sm">No services yet.</div>
            ) : (
              <ul className="space-y-2" data-testid="top-services">
                {data.topServices.map((s, i) => (
                  <li key={s.serviceId} className="flex items-center gap-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`top-service-${i}`}>
                    <div className="text-amber-500 text-sm font-serif w-4">#{i + 1}</div>
                    <div className="flex-1">
                      <div className="text-sm text-amber-100 line-clamp-1">{s.title}</div>
                      <div className="text-xs text-amber-200/50">{s.count} booking{s.count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-amber-100 text-sm font-serif">${s.gross.toFixed(2)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <p className="text-xs text-amber-300/40 text-center">
          Avg. bookings per client: {data.retention.avgBookingsPerClient} · Platform fees: ${data.earnings.totalFees.toFixed(2)}
        </p>
      </div>
    </Layout>
  );
}

function KpiTile({ testid, icon, label, value, hint }) {
  return (
    <Card className="p-4 bg-neutral-900/50 border-neutral-800" data-testid={testid}>
      <div className="flex items-center gap-2 text-amber-400 mb-2">
        {icon}
        <div className="text-xs text-amber-200/60 uppercase tracking-wider">{label}</div>
      </div>
      <div className="text-2xl font-serif text-amber-50">{value}</div>
      {hint && <div className="text-xs text-amber-200/50 mt-1">{hint}</div>}
    </Card>
  );
}
